import type { ComponentPropsWithAs } from '@/@types/common'
import type { Topic as TopicType } from '@/@types/topic'
import CloseIcon from '@/assets/close.svg?react'
import TopicIconBlueIcon from '@/assets/topic-blue.svg?react'
import clsx from 'clsx'
import { debounce, isEmpty } from 'lodash-es'
import { type ElementType, useRef } from 'react'
import ClickWrapper from './ClickWrapper'
import styles from './Topic.module.css'

export interface TopicProps {
	data: TopicType | undefined
	cleanable?: boolean
	onClean?: () => void
	onSearch?: (keyword: string) => void
}

export default function Topic<TElement extends ElementType = 'div'>({
	as: As,
	data,
	cleanable = false,
	onClean,
	onSearch,
	className,
	...props
}: ComponentPropsWithAs<TopicProps, TElement>) {
	const isInputCompositingRef = useRef(false)
	const handleChangeRef = useRef(
		debounce(
			(value) => {
				onSearch?.(value)
			},
			300,
			{
				trailing: true,
			}
		)
	)
	if (!data && !cleanable) {
		return null
	}
	const isPlaceholderDisplay = isEmpty(data?.content)
	const Component = As ?? 'div'
	return (
		<Component className={clsx(styles.root, className)} {...props}>
			<TopicIconBlueIcon className={styles.icon} />
			<div
				className={clsx(
					styles.content,
					isPlaceholderDisplay ? styles.contentPlaceholder : styles.contentNormal
				)}
			>
				{data?.content ?? (
					<input
						placeholder="未选择圈子"
						onChange={(e) => {
							if (isInputCompositingRef.current) {
								return
							}
							handleChangeRef.current(e.target.value)
						}}
						onCompositionStart={() => {
							isInputCompositingRef.current = true
						}}
						onCompositionEnd={(e) => {
							isInputCompositingRef.current = false
							handleChangeRef.current((e.target as HTMLInputElement).value)
						}}
						autoComplete="off"
						className={styles.input}
					/>
				)}
			</div>
			{cleanable && !isPlaceholderDisplay && (
				<ClickWrapper onClick={onClean} className={styles.cleanButton}>
					<CloseIcon className={styles.closeIcon} />
				</ClickWrapper>
			)}
		</Component>
	)
}
